import React, { useEffect, useRef, useState } from 'react';
import axios from '../../Utils/Axios';
import { supportedValues, fieldsFor, errorMessage, requestHeaders } from './sectionLayouts';

const previewScript = `<style>
[data-preview-item]{position:relative;outline:1px dashed #adc5e5;outline-offset:3px;min-height:52px}
[data-preview-item]::before{content:attr(data-preview-label);display:block;width:fit-content;max-width:100%;background:#eaf1ff;color:#254675;font:600 12px/1.5 sans-serif;padding:4px 8px;border-radius:4px;margin-bottom:8px}
[data-preview-item].preview-selected::before{content:attr(data-preview-label) " · Editing"!important;background:#2563eb;color:#fff}
</style><script>
(function(){
 let current='';
 document.querySelectorAll('[data-preview-section]').forEach((section, sectionIndex)=>{
  section.querySelectorAll('[data-preview-item]').forEach((item,index)=>{item.dataset.previewLabel='Section '+(sectionIndex+1)+' · Item '+(index+1);});
 });
 function select(section,item,scroll){
  document.querySelectorAll('.preview-selected').forEach(el=>el.classList.remove('preview-selected'));
  const el=item ? Array.from(document.querySelectorAll('[data-preview-item]')).find(el=>el.dataset.previewItem===String(item)&&el.dataset.previewOwner===String(section)) : document.getElementById('section-'+section);
  parent.postMessage({type:'ftt-preview-located',found:!!el},'*');
  if(el){el.classList.add('preview-selected');if(scroll)window.scrollTo(0,Math.max(0,el.getBoundingClientRect().top+window.scrollY-90));}
 }
 window.addEventListener('message',event=>{if(event.source!==parent||event.data?.type!=='ftt-preview-focus')return;const d=event.data;const next=String(d.section)+':'+String(d.item);select(d.section,d.item,d.scroll||next!==current);current=next;if(d.top)window.scrollTo(0,0);});
 document.addEventListener('click',event=>{event.preventDefault();const item=event.target.closest('[data-preview-item]');const section=event.target.closest('[data-preview-section]');if(section)parent.postMessage({type:'ftt-preview-select',section:section.dataset.previewSection,item:item?.dataset.previewItem||null},'*');},true);
 document.addEventListener('submit',event=>event.preventDefault(),true);
 parent.postMessage({type:'ftt-preview-ready'},'*');
})();
</script>`;

const imageData = file => new Promise((resolve, reject) => {
    if (file.size > 5 * 1024 * 1024) { reject(new Error('For live image preview, choose an image smaller than 5 MB.')); return; }
    if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(file.type)) { reject(new Error('Live image preview supports PNG, JPG, GIF and WebP.')); return; }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Unable to preview this image.'));
    reader.readAsDataURL(file);
});
const serialize = async value => ({
    id: String(value.id), sort_order: Number(value.sort_order || 0),
    ...Object.fromEntries(['heading', 'content', 'alt'].filter(key => key in value).map(key => [key, value[key] || ''])),
    ...(value.image instanceof File ? { image_preview: await imageData(value.image) } : {}),
});

export default function ServicePagePreview({ pageId, section, items, selectedSection, selectedItem, onSelect, onMeta, selectedLabel, locateRequest = 0, visible = true }) {
    const frame = useRef(null);
    const canvas = useRef(null);
    const callbacks = useRef({ onSelect, onMeta });
    const focus = useRef({ selectedSection, selectedItem });
    callbacks.current = { onSelect, onMeta };
    focus.current = { selectedSection, selectedItem };
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState('');
    const [device, setDevice] = useState(() => window.matchMedia?.('(max-width: 600px)').matches ? 390 : 1280);
    const [available, setAvailable] = useState(600);
    const [retry, setRetry] = useState(0);
    const [located, setLocated] = useState(null);
    const sendFocus = extras => frame.current?.contentWindow?.postMessage({ type: 'ftt-preview-focus', section: focus.current.selectedSection, item: focus.current.selectedItem, ...extras }, '*');
    useEffect(() => {
        let frameId = null;
        let pendingWidth = 0;
        let appliedWidth = 0;
        let active = true;
        const observer = new ResizeObserver(entries => {
            // Never resize the iframe during ResizeObserver delivery. Coalesce
            // measurements into the next frame and ignore subpixel jitter.
            const width = Math.floor(entries[0]?.contentRect.width || 0);
            if (!active || width <= 0) return; // The preview may be hidden in edit-only mode.
            pendingWidth = width;
            if (frameId !== null || width === appliedWidth) return;
            frameId = requestAnimationFrame(() => {
                frameId = null;
                if (!active || pendingWidth === appliedWidth) return;
                appliedWidth = pendingWidth;
                setAvailable(appliedWidth);
            });
        });
        observer.observe(canvas.current);
        return () => {
            active = false;
            observer.disconnect();
            if (frameId !== null) cancelAnimationFrame(frameId);
        };
    }, []);
    useEffect(() => {
        let active = true;
        setBusy(true); setError('');
        const timer = setTimeout(async () => {
            try {
                const payload = { page_id: pageId };
                if (section) { payload.section = await serialize(supportedValues(section, section.section_id, 'section')); payload.section.id = Number(section.id); payload.items = await Promise.all((fieldsFor(section.section_id, 'item').length ? items || [] : []).map(item => serialize(supportedValues(item, section.section_id, 'item')))); }
                const response = await axios.post('admin/service_page_preview', payload, { headers: requestHeaders() });
                if (active) { setResult(response.data); callbacks.current.onMeta?.(response.data); }
            } catch (failure) { if (active) setError(failure.response ? errorMessage(failure) : failure.message || 'Preview could not load.'); }
            finally { if (active) setBusy(false); }
        }, 600);
        return () => { active = false; clearTimeout(timer); };
    }, [pageId, section, items, retry]);
    useEffect(() => { if (visible) sendFocus({ scroll: true }); }, [selectedSection, selectedItem, locateRequest, visible, device]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        const receive = event => {
            if (event.source !== frame.current?.contentWindow) return;
            if (event.data?.type === 'ftt-preview-located') setLocated(event.data.found === true);
            if (event.data?.type === 'ftt-preview-ready') sendFocus({ scroll: true });
            if (event.data?.type === 'ftt-preview-select') callbacks.current.onSelect?.(event.data.section, event.data.item);
        };
        window.addEventListener('message', receive);
        return () => window.removeEventListener('message', receive);
    }, []);
    const scale = Math.min(1, Math.max(1, available) / device);
    const document = result?.html ? result.html.replace('</body>', `${previewScript}</body>`) : '';
    return <section className="sp-preview" aria-label="Frontend page preview">
        <div className="sp-preview-heading"><div><h3>Website preview</h3><p>3. Check how your changes look.</p></div><span className={`sp-status ${busy ? 'is-pending' : ''}`} role="status">{busy ? 'Updating…' : error ? 'Unavailable' : 'Draft ready'}</span></div>
        <div className="sp-preview-tools"><div className="sp-segment" aria-label="Preview screen size">{[[1280, 'Desktop'], [768, 'Tablet'], [390, 'Mobile']].map(([width, name]) => <button key={width} type="button" aria-pressed={device === width} onClick={() => setDevice(width)}>{name}</button>)}</div><button type="button" className="sp-text-button" onClick={() => sendFocus({ top: true })}>Page top</button><button type="button" className="sp-text-button" onClick={() => sendFocus({ scroll: true })}>Find selection</button></div>
        <div className="sp-preview-selection" aria-live="polite"><strong>{selectedLabel || 'Page preview'}</strong><span>{selectedItem === 'new' && !items?.some(item => item.id === 'new') ? 'Add content to show this item in the preview.' : located === false ? 'This item is not in the current preview yet. Add content or retry the preview.' : 'Blue outline marks your selection. Click a numbered item to edit it.'}</span></div>
        <div className="sp-preview-notice">Preview only. Save your changes to update the website.</div>
        {error && <div className="sp-error" role="alert">{error} <button type="button" onClick={() => setRetry(value => value + 1)}>Retry preview</button></div>}
        <div className="sp-preview-canvas" ref={canvas} aria-busy={busy}>
            {!result && <div className="sp-preview-placeholder">{error ? 'Preview is unavailable.' : 'Loading the actual service page…'}</div>}
            {result && <div className="sp-preview-viewport" style={{ width: device * scale, height: 610 }}><iframe ref={frame} title="Service website preview" onLoad={() => sendFocus({ scroll: true })} sandbox="allow-scripts" srcDoc={document} style={{ width: device, height: 610 / scale, transform: `scale(${scale})` }} /></div>}
            {busy && result && <div className="sp-preview-updating">Updating draft preview…</div>}
        </div>
        <div className="sp-preview-footer"><span>{device}px viewport · {Math.round(scale * 100)}% scale</span>{result?.page_url && <a href={`${result.page_url}${selectedSection ? '#section-' + selectedSection : ''}`} target="_blank" rel="noopener noreferrer">Open saved page ↗</a>}</div>
    </section>;
}
